import React, { Component } from "react";
import * as PropTypes from "prop-types";
import { Affix, Badge, Button, Checkbox, Divider, Input, Pagination, Select, Spin, Typography } from "antd";
import QueueAnim from "rc-queue-anim";
import UIEmptyState from "../EmptyState";
import UIInfoBanner from "../InfoBanner";
import UIKpiCards from "../KpiCards";

class UIPageListing extends Component {
	static propTypes = {
		onSearch             : PropTypes.func,
		onSearchChange       : PropTypes.func,
		onPaginationChange   : PropTypes.func,
		onOrderChange        : PropTypes.func,
		onListTypeChange     : PropTypes.func,
		onFiltersClick       : PropTypes.func,
		onSelectItem         : PropTypes.func,
		isLoading            : PropTypes.bool,
		theme                : PropTypes.oneOf(["default", "small"]),
		listType             : PropTypes.oneOf(["list", "card"]),
		showListTypeChange   : PropTypes.bool,
		orderByField         : PropTypes.string,
		orderBySort          : PropTypes.string,
		orders               : PropTypes.arrayOf(
			PropTypes.shape({
				label  : PropTypes.string.isRequired,
				field  : PropTypes.string.isRequired,
				sort   : PropTypes.string.isRequired,
				default: PropTypes.bool,
			}),
		),
		enableSearch         : PropTypes.bool,
		search               : PropTypes.string,
		searchPlaceholder    : PropTypes.string,
		data                 : PropTypes.array,
		enablePagination     : PropTypes.bool,
		pagination           : PropTypes.object,
		columns              : PropTypes.arrayOf(
			PropTypes.shape({
				title    : PropTypes.string,
				className: PropTypes.string,
				width    : PropTypes.any,
				visible  : PropTypes.bool,
				render   : PropTypes.func.isRequired,
			}),
		),
		buttons              : PropTypes.arrayOf(
			PropTypes.shape({
				visible: PropTypes.bool.isRequired,
				onClick: PropTypes.func.isRequired,
				title  : PropTypes.string,
				icon   : PropTypes.any,
				loading: PropTypes.bool,
				type   : PropTypes.oneOf(["primary", "secondary", "text", "default"]),
			}),
		),
		actions              : PropTypes.any,
		icon                 : PropTypes.node,
		title                : PropTypes.string,
		subtitle             : PropTypes.string,
		showFilters          : PropTypes.bool,
		allowSelect          : PropTypes.bool,
		selectedItems        : PropTypes.arrayOf(PropTypes.number),
		renderOnItemsSelected: PropTypes.any,
		hideHeaderOnSelect   : PropTypes.bool,
		emptyProps           : PropTypes.object,
		emptyIcon            : PropTypes.node,
		emptyTitle           : PropTypes.string,
		emptyDescription     : PropTypes.string,
		emptyAction          : PropTypes.node,
		appendSearch         : PropTypes.any,
		filtersCount         : PropTypes.number,
		infoBanner           : PropTypes.shape({
			icon       : PropTypes.node,
			title      : PropTypes.string,
			description: PropTypes.string,
		}),
		kpis                 : PropTypes.array,
		beforeKpis           : PropTypes.node,
		afterKpis            : PropTypes.node,
		stickyToolbarOnly    : PropTypes.bool,
		children             : PropTypes.node,
	};

	static defaultProps = {
		onSearch             : () => null,
		onSearchChange       : () => null,
		onPaginationChange   : () => null,
		onOrderChange        : () => null,
		onListTypeChange     : () => null,
		onFiltersClick       : () => null,
		onSelectItem         : () => null,
		isLoading            : false,
		theme                : "default",
		listType             : "list",
		showListTypeChange   : true,
		orderByField         : "",
		orderBySort          : "",
		orders               : [],
		enableSearch         : true,
		search               : "",
		searchPlaceholder    : "Buscar por nome",
		data                 : [],
		enablePagination     : true,
		pagination           : {},
		columns              : [],
		buttons              : [],
		actions              : [],
		showFilters          : false,
		allowSelect          : false,
		selectedItems        : [],
		renderOnItemsSelected: null,
		hideHeaderOnSelect   : true,
		emptyProps           : {},
		emptyIcon            : null,
		emptyTitle           : "",
		emptyDescription     : "",
		emptyAction          : null,
		appendSearch         : null,
		filtersCount         : 0,
		icon                 : null,
		title                : "",
		subtitle             : "",
		infoBanner           : null,
		kpis                 : [],
		beforeKpis           : null,
		afterKpis            : null,
		stickyToolbarOnly    : false,
	};

	state = {
		kpisCollapsed: false,
	};

	_rootRef = React.createRef();
	_headerQueueAnimRef = React.createRef();
	_titleRef = React.createRef();
	_resizeObserver = null;
	_scrollFrame = null;

	componentDidMount() {
		this._observeHeaderHeight();

		window.addEventListener("scroll", this._onScroll, { passive: true, capture: true });
	}

	/**
	 * Recolhe os cards de KPI quando a listagem rola por baixo do cabeçalho fixo
	 */
	_onScroll = () => {
		if( !this.props.kpis.length || this.props.stickyToolbarOnly || this._scrollFrame ) return;

		this._scrollFrame = window.requestAnimationFrame(() => {
			this._scrollFrame = null;

			const root       = this._rootRef.current;
			const headerNode = this._headerQueueAnimRef.current && this._headerQueueAnimRef.current.currentRef;

			if( !root || !headerNode ) return;

			const stickyTop = parseFloat(window.getComputedStyle(headerNode).top) || 0;
			const offset    = root.getBoundingClientRect().top - stickyTop;

			// histerese para não ficar abrindo/fechando no limite
			if( !this.state.kpisCollapsed && offset < -40 )
			{
				// segura a altura da página para o recolhimento não puxar o scroll de volta
				root.style.minHeight = `${root.offsetHeight}px`;
				this.setState({ kpisCollapsed: true });
			}
			else if( this.state.kpisCollapsed && offset > -5 )
			{
				root.style.minHeight = "";
				this.setState({ kpisCollapsed: false });
			}
		});
	};

	componentDidUpdate() {
		if( !this._resizeObserver )
		{
			this._observeHeaderHeight();
		}
	}

	_observeHeaderHeight = () => {
		const headerNode = this._headerQueueAnimRef.current && this._headerQueueAnimRef.current.currentRef;

		if( typeof ResizeObserver === "undefined" || !headerNode )
		{
			return;
		}

		const titleNode = this._titleRef.current;

		this._resizeObserver = new ResizeObserver((entries) => {
			if( !this._rootRef.current ) return;

			entries.forEach((entry) => {
				const variable = entry.target === titleNode ? "--page-listing-title-height" : "--page-listing-header-height";

				this._rootRef.current.style.setProperty(variable, `${entry.contentRect.height}px`);
			});
		});

		this._resizeObserver.observe(headerNode);

		if( titleNode )
		{
			this._resizeObserver.observe(titleNode);
		}
	};

	componentWillUnmount() {
		if( this._resizeObserver )
		{
			this._resizeObserver.disconnect();
		}

		window.removeEventListener("scroll", this._onScroll, { capture: true });

		if( this._scrollFrame )
		{
			window.cancelAnimationFrame(this._scrollFrame);
		}
	}

	_renderEmpty = () => {
		const { emptyIcon, emptyTitle, emptyDescription, emptyAction, emptyProps } = this.props;

		return (
			<UIEmptyState
				{...(emptyIcon ? { icon: emptyIcon } : {})}
				title={emptyTitle}
				description={emptyDescription}
				action={emptyAction}
				emptyProps={emptyProps}
			/>
		)
	};

	_renderList = () => {
		if( !this.props.data.length )
		{
			return (
				<React.Fragment>
					{this._renderEmpty()}
					{this.props.infoBanner && <UIInfoBanner {...this.props.infoBanner} />}
				</React.Fragment>
			);
		}

		const columns = this.props.columns.filter(column => !column.hasOwnProperty("visible") || column.visible);

		const {listType, allowSelect, selectedItems, enablePagination} = this.props;
		return (
			<div className={`list-items ${listType === "list" ? "list-type" : "card-type"}`}>
				{listType === "list" && <div className="list-items-header">
					<div className="row">
						{allowSelect && <div className="col col-select" data-title="" />}
						{columns.map((column, index) => {
							let columnProps = {
								key      : index,
								className: `col ${column.className || ""}`,
								style    : {},
							};

							if( column.width )
							{
								columnProps.style.maxWidth = column.width;
							}

							return (
								<div {...columnProps}>
									<div>{column.title}</div>
								</div>
							)
						})}
					</div>
				</div>}
				<QueueAnim type="bottom" className="list-items-body">
					{this.props.data.map((item, index) => (
						<div key={index} className={`list-items-item ${selectedItems.indexOf(item.id) !== -1 ? "selected" : ""}`}>
							<div className="row">
								{allowSelect && <div className="col col-select" data-title="">
									<div>
										<Checkbox
											onChange={(e) => this.props.onSelectItem(item, e.target.checked)}
											checked={selectedItems.indexOf(item.id) !== -1}
										/>
									</div>
								</div>}
								{columns.map((column, index) => {
									let columnProps = {
										key      : index,
										className: `col ${column.className || ""}`,
										style    : {},
									};

									if( column.width )
									{
										columnProps.style.maxWidth = column.width;
									}

									return (
										<div data-title={column.title} {...columnProps}>
											<div>{column.render(item)}</div>
										</div>
									)
								})}
							</div>
						</div>
					))}
				</QueueAnim>
				{this.props.infoBanner && <UIInfoBanner {...this.props.infoBanner} />}
				{enablePagination && <Pagination
					{...this.props.pagination}
					onChange={this.props.onPaginationChange}
					showSizeChanger
					showTotal={(total, range) => `${range[0]}-${range[1]} de ${total} itens`}
				/>}
			</div>
		)
	};

	_renderButtons = () => {
		return this.props.buttons.map((button, index) => button.visible && (
			<Button
				key={index}
				type={button.type === "secondary" ? "default" : (button.type || "primary")}
				className={`action-btn ${button.type === "secondary" ? "action-btn-secondary" : ""}`}
				icon={button.icon}
				disabled={button.loading}
				loading={button.loading}
				onClick={button.onClick}
			>
				{button.title}
			</Button>
		));
	};

	_renderTitle = () => {
		return (
			<React.Fragment>
				<div className="page-listing-title-row">
					<div className="page-listing-title-info">
						{this.props.icon && <div className="page-listing-title-icon">{this.props.icon}</div>}
						<div>
							<h1 className="page-listing-title">{this.props.title}</h1>
							{this.props.subtitle && <p className="page-listing-subtitle">{this.props.subtitle}</p>}
						</div>
					</div>
					<div className="page-listing-title-actions">
						{this._renderButtons()}
						{this.props.actions}
					</div>
				</div>
				<Divider className="page-listing-divider" />
			</React.Fragment>
		);
	};

	render() {
		const hasTitle = !!this.props.title;
		const hasKpis = this.props.kpis.length > 0;
		const showHeader = this.props.selectedItems.length === 0 || !this.props.hideHeaderOnSelect;
		const { stickyToolbarOnly } = this.props;

		const headerTop = (
			<React.Fragment>
				{hasTitle && !stickyToolbarOnly && this._renderTitle()}
				{this.props.beforeKpis}
				{hasKpis && <div className={`page-listing-kpis ${this.state.kpisCollapsed ? "page-listing-kpis-collapsed" : ""}`} aria-hidden={this.state.kpisCollapsed}>
					<UIKpiCards items={this.props.kpis} />
				</div>}
				{this.props.afterKpis}
			</React.Fragment>
		);

		return (
			<div ref={this._rootRef} className={`page-listing page-listing-${this.props.theme} ${hasTitle ? "page-listing-has-title" : ""} ${stickyToolbarOnly ? "page-listing-sticky-toolbar" : ""}`}>
				{/* filtros/KPIs rolam com a página; só o título e a barra de ações ficam fixos */}
				{showHeader && stickyToolbarOnly && hasTitle && <div ref={this._titleRef} className="page-listing-header-wrap page-listing-title-sticky">{this._renderTitle()}</div>}
				{showHeader && stickyToolbarOnly && <div className="page-listing-header-wrap page-listing-header-wrap-static">{headerTop}</div>}
				{showHeader && <QueueAnim ref={this._headerQueueAnimRef} componentProps={{ className: `page-listing-header-wrap ${stickyToolbarOnly ? "page-listing-toolbar-sticky" : ""}` }} type="alpha">
						<div key="1">
							{!stickyToolbarOnly && headerTop}
							<div className="page-listing-header">
								{this.props.appendSearch}
								{this.props.enableSearch && <div className="search">
									<Input
										placeholder={this.props.searchPlaceholder}
										size="large"
										prefix={<i className="fal fa-search" />}
										suffix={this.props.isLoading ? <i className="fad fa-spinner-third fa-spin" /> : null}
										onChange={this.props.onSearchChange}
										allowClear
										onPressEnter={(e) => {
											// Prevent parent form submit
											e.preventDefault();

											// Search
											this.props.onSearch(e.target.value);

											return false;
										}}
									/>
								</div>}
								{this.props.orders.length > 0 && <div className="order">
									<Select size="large" onChange={this.props.onOrderChange} value={`${this.props.orderByField}:${this.props.orderBySort}`}>
										{this.props.orders.map((item, index) => (
											<Select.Option key={index} value={`${item.field}:${item.sort}`}>{item.label}</Select.Option>
										))}
									</Select>
								</div>}
								<div className="btns">
									{this.props.showListTypeChange && <Button.Group size="large" className="btn-group-list-type">
										<Button type="default" className={`action-btn ${this.props.listType === "list" ? "active" : "action-btn-secondary"}`} icon={<i className="fas fa-list-ul" />} onClick={() => this.props.onListTypeChange("list")} />
										<Button type="default" className={`action-btn ${this.props.listType === "card" ? "active" : "action-btn-secondary"}`} icon={<i className="fas fa-th" />} onClick={() => this.props.onListTypeChange("card")} />
									</Button.Group>}
									{this.props.showFilters && <Button size="large" type="default" className="action-btn action-btn-secondary" icon={<i className="fal fa-filter" />} onClick={this.props.onFiltersClick}><Badge count={this.props.filtersCount} showZero={false} offset={[8, -5]}>Filtros</Badge></Button>}
									{!hasTitle && <div style={{flex: 1}} />}
									{!hasTitle && this._renderButtons()}
									{!hasTitle && this.props.actions}
								</div>
							</div>
						</div>
				</QueueAnim>}
				{this.props.allowSelect && this.props.selectedItems.length > 0 && this.props.renderOnItemsSelected && <QueueAnim type="alpha">
					<div key="1" className="page-listing-header-selected-items">
						{this.props.renderOnItemsSelected}
					</div>
				</QueueAnim>}
				{this.props.search && <Typography.Paragraph>Buscando por <Typography.Text mark>{this.props.search}</Typography.Text></Typography.Paragraph>}
				<Spin spinning={this.props.isLoading} indicator={<i className="fad fa-spinner-third fa-spin fa-3x" />}>
					{this.props.children ?? this._renderList()}
				</Spin>
			</div>
		)
	}
}

export default UIPageListing;
